import $ from 'jquery';

import AppleView from 'chairisher/view/apple';
import AuthenticationView from 'chairisher/view/authentication';
import FacebookView from 'chairisher/view/facebook';
import ModalFactory from 'chairisher/factory/modal';
import SmsModal from 'chairisher/view/promo/smsmodal';

import renderGoogleSignin from 'chairisher/view/socialauth/google';

import { trackAccountCreated } from 'chairisher/analytics/auth';
import {
    trackEmailModalCollectionAttempt,
    trackSuccessfulEmailModalCollection,
    trackUnsuccessfulEmailModalCollection,
} from 'chairisher/analytics/email';
import { trackSmsModalShow } from 'chairisher/analytics/sms';
import { getQuickCreateUrl, setGuid } from 'chairisher/context/auth';
import {
    getMarketingEmailUrl,
    isMarketingEmailModalVisible,
    setMarketingEmailModalVisible,
} from 'chairisher/context/promo';
import {
    AmplitudeEventProperty,
    ModalPosition,
    logAmplitudeClickEvent,
    logAmplitudeEvent,
} from 'chairisher/util/analytics';
import { submitForm } from 'chairisher/util/fetch';
import { setEmailModalOptOutCookie } from 'chairisher/view/helper/marketingpromo';
import { executeRecaptchaForForm } from 'chairisher/view/helper/recaptcha';

/**
 * The DOM selector for the email collection form
 *
 * @type {string}
 */
const FORM_SELECTOR = '#js-chairish-email-collector-form';

/**
 * The DOM ID for the email collection modal
 *
 * @type {string}
 */
const MODAL_ID = 'js-chairish-email-modal';

/**
 * Opts a user out of seeing the email collection form for a set number of days (defaults to MemberCookieDurationInDays.DEFAULT)
 */
function optOut() {
    setEmailModalOptOutCookie();
    trackUnsuccessfulEmailModalCollection();
}

/**
 * Form that blocks usage of the site and collects email addresses when the
 * viewer is not logged in and has visited more than one page.
 */
class MarketingEmailModal {
    constructor() {
        this.shouldBeDismissible = true;
        this.$modal = null;
    }

    /**
     * Returns the element containing the Email Modal
     * @returns {jquery}
     */
    getEl() {
        return $(`#${MODAL_ID}`);
    }

    /**
     * Hides the modal
     */
    hide() {
        if (isMarketingEmailModalVisible()) {
            this.getEl().modal('hide');
        }
    }

    /**
     * Shows the modal
     *
     * @returns {$.Deferred}
     */
    show() {
        const $deferred = $.Deferred();

        if (!isMarketingEmailModalVisible()) {
            if (!this.$modal) {
                this.populateContent()
                    .done(() => {
                        this.$modal.modal('show');
                        $deferred.resolve();
                    })
                    .fail(() => {
                        $deferred.reject(
                            new Error('Action failed: Unable to populate content for marketing email modal.'),
                        );
                    });
            } else {
                this.$modal.modal('show');
                $deferred.reject(new Error('Action failed: Marketing email modal already exists.')); // don't need to rebind third party thingies
            }
        } else {
            $deferred.reject(new Error('Action failed: Marketing email modal already visible.'));
        }

        return $deferred;
    }

    /**
     * Submits the form
     *
     * @param {function=} successCallback
     * @param {function=} errorCallback
     */
    async submit(successCallback, errorCallback) {
        const $form = $(FORM_SELECTOR);

        try {
            await executeRecaptchaForForm($form[0]);

            const data = await submitForm($form[0], {
                capture_type: 'modal',
                path_name: document.location.pathname,
            });

            setEmailModalOptOutCookie();
            this.hide();

            if (data.guid) {
                setGuid(data.guid);
            }

            if (data.was_account_created) {
                trackSuccessfulEmailModalCollection(this.shouldBeDismissible);
                trackAccountCreated();
            }

            if (successCallback) {
                successCallback();
            }
        } catch (error) {
            if (errorCallback && error.json) {
                errorCallback(error.json);
            } else {
                throw error;
            }
        }
    }

    /**
     * Fetches and displays the content if possible.
     *
     * @returns {$.Deferred}
     * @private
     */
    populateContent() {
        const getParameters = { path: window.location.pathname };

        return $.ajax({
            url: getMarketingEmailUrl(),
            data: getParameters,
        }).done((data) => {
            // create a modal to house the email collection form
            const $modal = ModalFactory.createModal(MODAL_ID);
            $modal.find('.modal-body').html(data.html);

            this.shouldBeDismissible = $modal.find('.js-is-gated').val() !== 'True';

            // we now have enough information to decide whether to display the modal
            $modal.appendTo(document.body);

            const authView = new AuthenticationView({
                formSelector: '.js-chairish-email-collector-form',
                shouldBeDismissible: false,
            });

            if (this.shouldBeDismissible) {
                $modal.modal({
                    backdrop: true,
                    keyboard: true,
                    show: false,
                });
            } else {
                const $modalHeader = $modal.find('.js-modal-header');
                $modalHeader.remove();
                $modal.modal({
                    backdrop: 'static',
                    keyboard: false,
                    show: false,
                });
            }

            // handle sign up form submission
            $modal.on('submit', FORM_SELECTOR, (e) => {
                e.preventDefault();
                this.submit(
                    () => {
                        const smsModal = new SmsModal();
                        smsModal.show().done(() => {
                            trackSmsModalShow();
                        });
                    },
                    (responseJson) => {
                        $modal.find('.modal-body').html(responseJson.html);
                    },
                );
            });

            // handle log in link click
            $modal.on('click', '.js-log-in', ({ preventDefault }) => {
                preventDefault();

                const url = getQuickCreateUrl('marketing-email-modal');

                logAmplitudeClickEvent('log in', ModalPosition.AUTH, url);

                this.hide();

                // Show the log in modal
                authView
                    .display({
                        url: `${url}&is_sign_up=0`,
                        shouldBeDismissible: false,
                    })
                    .done(() => {
                        AppleView.loadAuthButton();
                        FacebookView.bind(); // for facebook connect login button
                        renderGoogleSignin();

                        const $authViewModal = $(authView.getModalSelector());

                        const toggleModals = () => {
                            $authViewModal.find('.js-auth-toggle').on('click', 'a', (e) => {
                                e.preventDefault();

                                logAmplitudeClickEvent(
                                    'log in',
                                    ModalPosition.AUTH,
                                    $authViewModal.find('form').attr('action'),
                                );

                                authView.hide();
                                this.show();
                            });
                        };
                        toggleModals();

                        // On submit, log in the user and then submit the email marketing form with the logged in email
                        $authViewModal.on('submit', 'form', (e) => {
                            e.preventDefault();
                            const $form = $(e.target);

                            authView.submit(
                                $form.attr('action'),
                                $form.serialize(),
                                (successData) => {
                                    logAmplitudeEvent('account - login', {
                                        [AmplitudeEventProperty.CTA_POSITION]: ModalPosition.AUTH,
                                        [AmplitudeEventProperty.LOGIN_TYPE]: successData.login_type,
                                    });

                                    authView.hide();
                                    $(FORM_SELECTOR).find('#id_email').val(successData.email);

                                    this.submit();
                                },
                                (errorData) => {
                                    authView.setFormHtml(errorData.authentication_form_html);

                                    AppleView.loadAuthButton();
                                    FacebookView.bind();
                                    renderGoogleSignin();
                                    toggleModals();
                                },
                            );
                        });
                    });
            });

            // mark the modal as being open
            $modal.on('show.bs.modal', () => {
                setMarketingEmailModalVisible(true);
            });

            // track the collection attempt
            $modal.on('shown.bs.modal', () => {
                trackEmailModalCollectionAttempt(this.shouldBeDismissible);
            });

            // set the opt-out cookie when the modal is about to hide
            $modal.on('hide.bs.modal', () => {
                optOut();
                setMarketingEmailModalVisible(false);
                renderGoogleSignin(false);
            });

            // disallow double-clicks on the form submit button
            $(FORM_SELECTOR).buttonLoadingHelper();

            this.$modal = $modal;
        });
    }
}

export default function showMarketingEmailModal() {
    new MarketingEmailModal().show().done(() => {
        AppleView.loadAuthButton();
        FacebookView.bind();
        renderGoogleSignin();
    });
}
